import {computed, onMounted, onUnmounted, ref, useRoute} from "#imports";
import {useLearningStore} from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import {useLearningService} from "~/composables/service/useLearningService";

export enum DomainType {
    TRAINING = 'TRAINING',
    LEVEL_ASSESSMENT = 'LEVEL_ASSESSMENT',
    CONTEST = 'CONTEST',
    CUSTOM_ASSESSMENT = 'CUSTOM_ASSESSMENT'
}

// 전역 상태 관리 (싱글톤)
let globalIntervalId: number | NodeJS.Timeout | null = null;
let globalStartedAtMs: number = 0;
let globalIsFlushing: boolean = false;
let globalFlushTimeoutId: number | NodeJS.Timeout | null = null;
const globalCurrentOptions: { intervalMs: number } = { intervalMs: 30000 };
let globalCurrentTaskId: number = 0;
let globalCurrentDomainData: { domain: DomainType; domainId: number } | null = null;
let globalIsStopped: boolean = false; // 중복 호출 방지 플래그

export const useTaskDurationTrackingService = () => {
    const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
    const { saveTaskDuration } = useLearningService();
    const route = useRoute();

    const domainData = computed(() => {
        if (learningStore.isLevelAssessment) return {domain: DomainType.LEVEL_ASSESSMENT, domainId: parseInt(route.params.levelId as string)}
        else if (learningStore.isProSkillAssure) return {domain: DomainType.CUSTOM_ASSESSMENT, domainId: assessmentStore.proSkillAssure.assessmentId}
        else if (learningStore.isContest) return {domain: DomainType.CONTEST, domainId: assessmentStore.contestId}
        else return {domain: DomainType.TRAINING, domainId: learningStore.course.id as number}
    });

    const startTracking = (taskId: number) => {
        stopTracking();
        globalIsStopped = false; // 시작 시 플래그 리셋
        globalCurrentTaskId = taskId;
        globalCurrentDomainData = domainData.value;
        globalStartedAtMs = Date.now(); // 시작 시간 설정
        globalIntervalId = setInterval(async () => {
            try {
                if (globalCurrentTaskId && globalCurrentDomainData && !globalIsStopped) {
                    const intervalSec = Math.floor(globalCurrentOptions.intervalMs / 1000);
                    await saveTaskDuration(intervalSec, globalCurrentDomainData.domain, globalCurrentDomainData.domainId, globalCurrentTaskId);
                }
                globalStartedAtMs = Date.now();
            } catch (e) {}
        }, globalCurrentOptions.intervalMs);
    };

    const updateTask = (newTaskId: number) => {
        if (globalCurrentTaskId !== newTaskId) {
            // 이전 task의 잔여 시간을 먼저 전송
            if (globalStartedAtMs && globalCurrentTaskId && globalCurrentDomainData) {
                const elapsedSec = Math.max(0, Math.floor((Date.now() - globalStartedAtMs) / 1000));
                if (elapsedSec > 0) {
                    saveTaskDuration(elapsedSec, globalCurrentDomainData.domain, globalCurrentDomainData.domainId, globalCurrentTaskId);
                }
            }
            // 새 task로 시작
            globalCurrentTaskId = newTaskId;
            globalCurrentDomainData = domainData.value;
            globalStartedAtMs = Date.now();
        }
    };

    const updateInterval = (newIntervalMs: number) => {
        if (newIntervalMs < 1000) return; // 최소 1초
        
        globalCurrentOptions.intervalMs = newIntervalMs;
        
        if (globalIntervalId) {
            // 현재 인터벌이 실행 중이면 재시작
            stopTracking();
            startTracking(globalCurrentTaskId);
        }
    };

    const getCurrentOptions = () => ({ ...globalCurrentOptions });

    const stopTracking = async () => {
        // 이미 중지된 경우 중복 호출 방지
        if (globalIsStopped) return;

        globalIsStopped = true; // 중지 플래그 설정
        
        if (globalIntervalId) {
            clearInterval(globalIntervalId as number);
            globalIntervalId = null;
        }
        if (globalFlushTimeoutId) {
            clearTimeout(globalFlushTimeoutId as number);
            globalFlushTimeoutId = null;
        }
        await flushRemainingDuration();
    };

    const flushRemainingDuration = async () => {
        if (!globalStartedAtMs || globalIsFlushing) return;
        globalIsFlushing = true;
        const elapsedSec = Math.max(0, Math.floor((Date.now() - globalStartedAtMs) / 1000));
        if (elapsedSec > 0 && globalCurrentTaskId && globalCurrentDomainData) {
            try {
                await saveTaskDuration(elapsedSec, globalCurrentDomainData.domain, globalCurrentDomainData.domainId, globalCurrentTaskId);
            } catch (e) {}
        }
        globalStartedAtMs = Date.now();
        globalIsFlushing = false;
    };

    onMounted(() => {});
    onUnmounted(() => {
        stopTracking();
    });

    return {
        startTracking,
        stopTracking,
        updateInterval,
        getCurrentOptions,
        updateTask,
    };
}

